// U32b — các mảnh vẽ của bước gộp «Prompt & Video» (3 cột 3 cột). Logic ở promptVideoUtils / videoStepRowUtils (thuần, có test); file này chỉ vẽ.
import React, { useEffect, useRef, useState } from 'react';
import type { FilmEvent } from '../../types/film';
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import { formatClock } from './workflowBoardUtils';
import { clipCardsOf, type ClipCard } from './videoStepRowUtils';
import { clipGenInfoOf, clipGenShortOf, playingClipOf } from './workflowClipInfo';   // 261029 S4 (Rev 3)
import ClipPromptFold from './ClipPromptFold';
import { sceneListOf, videoListStatusOf, type PromptChip, type VideoListStatus } from './promptVideoUtils';
import { IconChip } from './StatusIcon';
import { statusIconOf, type IconName } from './workflowStatusIcons';
import WfIcon from './WfIcon';
import WfIconButton from './WfIconButton';
import { ViewableImg } from './ImageViewer';

/** Khung đầu của clip: chỉ nạp khi cuộn tới (nhiều video cùng lúc không tải hết). */
export const LazyFrame: React.FC<{ url: string }> = ({ url }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || src) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setSrc(url); return undefined; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setSrc(url); io.disconnect(); } }, { rootMargin: '120px' });
    io.observe(el);
    return () => io.disconnect();
  }, [url, src]);
  return <video ref={ref} src={src ? `${url}#t=0.1` : undefined} preload="metadata" muted playsInline tabIndex={-1} aria-hidden="true" />;
};

/** Một ô biểu tượng trạng thái (✓ ⏳ ✕ ! ○) với nhãn tiếng Việt của mục video. */
export const VideoStatusIcon: React.FC<{ st: VideoListStatus; testId?: string }> = ({ st, testId }) => {
  const v = statusIconOf(st.chipText, st.tone);
  return <IconChip label={st.label} icon={v.icon} tone={v.tone} value={null} focusable={false} testId={testId} />;
};

/** Cột trái: danh sách video nhóm theo cảnh. */
export const VideoList: React.FC<{
  events: WorkflowEventStatus[]; selectedId: string | null; evOf: (eventId: string) => FilmEvent | null | undefined; onSelect: (eventId: string) => void;
}> = ({ events, selectedId, evOf, onSelect }) => (
  <nav className="wf-pv-list" aria-label="Danh sách video" data-testid="wf-pv-list">
    {sceneListOf(events).map((g) => (
      <div key={g.sceneId} className="wf-pv-sc">
        <div className="wf-pv-sch"><b>{g.title}</b>{g.heading ? <span title={g.heading}>{g.heading}</span> : null}</div>
        {g.events.map((e) => {
          const st = videoListStatusOf(e, evOf(e.eventId) ?? null);
          const on = e.eventId === selectedId;
          return (
            <button key={e.eventId} type="button" className={`wf-pv-li${on ? ' on' : ''}`} aria-current={on ? 'true' : undefined} onClick={() => onSelect(e.eventId)}
              aria-label={`Video ${e.eventOrder} — ${st.label}`} data-testid={`wf-pv-li-${e.eventOrder}`}>
              <b>V{e.eventOrder}</b>
              <VideoStatusIcon st={st} testId={`wf-pv-st-${e.eventOrder}`} />
              {e.genDurationS ? <span className="mono">{formatClock(e.genDurationS)}</span> : null}
            </button>
          );
        })}
      </div>
    ))}
  </nav>
);

/** Dải chip tham chiếu đang gửi kèm (ảnh nhân vật / bối cảnh / đạo cụ có trong prompt + storyboard / sơ đồ của video). */
export const RefChipStrip: React.FC<{ chips: PromptChip[]; extra: { label: string; url: string } | null }> = ({ chips, extra }) => {
  if (!chips.length && !extra) return null;
  return (
    <div className="wf-pv-refs" aria-label="Tham chiếu gửi kèm" data-testid="wf-pv-refs">
      {chips.map((c) => (
        <span key={c.key} className={`wf-pv-chip${c.missing ? ' bad' : c.ready ? '' : ' dim'}`}
          title={c.missing ? `${c.label} — không còn trong danh sách tài sản` : c.ready ? c.label : `${c.label} — chưa có ảnh, engine chỉ nhận mô tả bằng chữ`}>
          {c.url ? <ViewableImg group="video-refs" src={c.url} alt={c.label} loading="lazy" /> : <WfIcon name={c.missing ? 'warning' : 'picture'} />}<i>{c.label}</i>
        </span>
      ))}
      {extra ? <span className="wf-pv-chip" title={extra.label}><ViewableImg group="video-refs" src={extra.url} alt={extra.label} loading="lazy" /><i>{extra.label}</i></span> : null}
    </div>
  );
};

const KIND_ICON: Record<ClipCard['kind'], IconName> = { DONE: 'video', RUNNING: 'loading', ERROR: 'close', OTHER: 'warning' };

/** Dải bản clip nhỏ: bấm thẻ = xem ở player; ★ = chọn bản (hoặc mở chi tiết khi video có phiếu AI chấm). */
export const VersionStrip: React.FC<{
  eventId: string; order: number; ev: FilmEvent | null; focusId: string | null; busyClipId: string | null; viaDrawer: boolean;
  onFocus: (clipId: string) => void; onPick: (clipId: string) => void;
}> = ({ eventId, order, ev, focusId, busyClipId, viaDrawer, onFocus, onPick }) => {
  const cards = clipCardsOf(ev);
  if (!cards.length) return <p className="muted wf-pv-none">Chưa có bản clip nào.</p>;
  const clipById = new Map((ev?.clips ?? []).map((c) => [c.id, c]));
  return (
    <div className="wf-pv-vers" role="list" aria-label={`Các bản clip của Video ${order}`} data-testid="wf-pv-vers">
      {cards.map((c) => (
        <div key={c.id} role="listitem" className={`wf-pv-t ${c.kind.toLowerCase()}${c.selected ? ' sel' : ''}${focusId === c.id ? ' focus' : ''}`} data-testid={`wf-pv-t-${c.id}`}>
          <button type="button" className="wf-pv-tv" disabled={!c.canPlay} aria-label={`Xem ${c.label}`} onClick={() => onFocus(c.id)}>
            {c.canPlay && c.url ? <LazyFrame url={c.url} /> : <span className="ph" role="img" aria-label={c.note ?? c.label}><WfIcon name={KIND_ICON[c.kind]} /></span>}
          </button>
          <span className="wf-pv-ts">
            {c.selected ? <span className="on" role="img" aria-label="Đang dùng"><WfIcon name="starFilled" /></span>
              : c.canPick ? <WfIconButton icon="star" label={viaDrawer ? 'Chọn ở Chi tiết' : 'Chọn bản này'} why={viaDrawer ? 'Video này đã có phiếu AI chấm clip — chọn bản ở ngăn chi tiết (có hộp ghi lý do)' : undefined}
                loading={busyClipId === c.id} disabled={busyClipId !== null && busyClipId !== c.id} onClick={() => onPick(c.id)} testId={`wf-pv-pick-${c.id}`} /> : null}
          </span>
          <div className="wf-pv-tf"><b>{c.label}</b>{c.durationS ? <span className="mono">{formatClock(c.durationS)}</span> : null}</div>
          {ev && clipById.get(c.id) ? (() => {
            const g = clipGenShortOf(clipById.get(c.id)!, ev);   // 261029 S4: bản này đã gen bằng gì (đọc clip.meta); lệch cấu hình hiện tại ⇒ chữ «lệch»
            return <div className="wf-pv-tg" title={g.full} data-testid={`wf-pv-tg-${c.id}`}>{g.short}{g.mismatch ? <span className="wf-pv-tw" title={g.mismatch} data-testid={`wf-pv-tw-${c.id}`}>lệch</span> : null}</div>;
          })() : null}
          {c.note ? <div className={`wf-pv-tn${c.kind === 'ERROR' ? ' bad' : ''}`} title={c.note}>{c.note}</div> : null}
        </div>
      ))}
    </div>
  );
};

/** Player 9:16 của clip đang xem (đang chọn trước, rồi bản xem thử). */
export const ClipPlayer: React.FC<{ url: string | null; st: VideoListStatus }> = ({ url, st }) => (
  url
    ? <video className="wf-pv-player" src={url} controls preload="metadata" playsInline data-testid="wf-pv-player" />
    : <div className="wf-pv-player empty" data-testid="wf-pv-empty"><WfIcon name={st.kind === 'RUNNING' ? 'loading' : st.kind === 'ERROR' ? 'close' : 'video'} /><span>{st.label}</span></div>
);


/** 261029 S4 (Rev 3) — dưới player: thông tin BẢN ĐANG PHÁT (focus → đang chọn → phát được đầu tiên, cùng thứ tự `playUrl`): đã gen bằng gì · nhãn lệch cấu hình · prompt đã gửi. Không có bản phát được ⇒ không hiện khối. */
export const ClipInfoBlock: React.FC<{ ev: FilmEvent | null; focusId: string | null }> = ({ ev, focusId }) => {
  const clip = playingClipOf(ev, focusId);
  if (!ev || !clip) return null;
  const info = clipGenInfoOf(clip, ev);
  return (
    <div className="wf-pv-info" data-testid="wf-pv-info">
      <div className="wf-pv-il"><span className="mono" data-testid="wf-pv-info-line">{info.line}</span>{info.mismatch ? <span className="wf-pv-tw big" title={info.mismatch} data-testid="wf-pv-info-mismatch">{info.mismatch}</span> : null}</div>
      {clip.prompt ? <ClipPromptFold prompt={clip.prompt} testIdPrefix="wf-pv-clip" /> : null}
    </div>
  );
};
